import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { post } from '../../common/constants'
import { formatDate1 } from '../../common/util'
import DateRangePicker from '../../components/DateRangePicker'
import OrderCreationLatencyTrend from './OrderCreationLatencyTrend'
import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import { useSnackbar } from 'notistack'

// ─── colour bands (avg latency, ms) ────────────────────────────────────────────
const BAND = {
    good: { bg: '#e7f4ea', ink: '#1e6b34', bar: '#35a054' },
    warn: { bg: '#fdf3dd', ink: '#8a5d07', bar: '#e0a52a' },
    crit: { bg: '#fdeae8', ink: '#a03024', bar: '#d9534a' },
    agg: { bg: '#eef1f6', ink: '#1e2530', bar: '#9aa5b5' },
}
// Order-creation realistically has a ~1s floor, so bands start higher: healthy <=2s, elevated 2-5s, critical >5s.
const bandOf = (avg) => (avg <= 2000 ? 'good' : avg <= 5000 ? 'warn' : 'crit')
const ALL = '__ALL__'
const API_FMT = "yyyy-MM-dd'T'HH:mm:ss"

// time-range chips (minutes)
const CHIPS = [
    { key: '5m', label: 'Last 5 min', minutes: 5 },
    { key: '15m', label: 'Last 15 min', minutes: 15 },
    { key: '2h', label: 'Last 2 hours', minutes: 120 },
    { key: '4h', label: 'Last 4 hours', minutes: 240 },
    { key: '15h', label: 'Last 15 hours', minutes: 900 },
]

// Stable empty-array reference: resetting filters to THIS (vs a fresh []) lets React's setState bail
// out when already empty, so the trend child's props keep identity and it doesn't refetch twice.
const EMPTY_IDS = []

const fmtNum = (v) => (v == null ? '—' : Math.round(v).toLocaleString('en-IN'))

export default function OrderCreationLatency() {
    const { enqueueSnackbar } = useSnackbar()

    const [chip, setChip] = useState('2h')
    const [range, setRange] = useState(() => {
        const now = new Date()
        return { from: new Date(now.getTime() - 120 * 60 * 1000), to: now, label: 'Last 2 hours' }
    })
    const [loading, setLoading] = useState(false)
    const [data, setData] = useState(null)
    // Filters are applied SERVER-side (refetch) so cells and aggregates — incl. percentiles — are exact.
    const [merchantFilter, setMerchantFilter] = useState(EMPTY_IDS)
    const [courierFilter, setCourierFilter] = useState(EMPTY_IDS)
    // Filter option universe, captured from the unfiltered "base" load of the current window, so the
    // dropdowns keep listing every merchant/courier even while the matrix is showing a filtered subset.
    const [optionMerchants, setOptionMerchants] = useState([])
    const [optionCouriers, setOptionCouriers] = useState([])
    const [downloadingKey, setDownloadingKey] = useState(null) // which cell's AWB CSV is being prepared
    const [customDates, setCustomDates] = useState(null) // last picked date range (for combining with times)
    const [startTime, setStartTime] = useState('') // optional HH:mm; blank = start of day
    const [endTime, setEndTime] = useState('')     // optional HH:mm; blank = end of day

    const applyChip = (c) => {
        const now = new Date()
        setChip(c.key)
        setRange({ from: new Date(now.getTime() - c.minutes * 60 * 1000), to: now, label: c.label })
    }

    // Combine the picked dates with the optional times (blank start = 00:00, blank end = 23:59:59).
    const buildCustomRange = (dates, sTime, eTime) => {
        if (!dates || !dates.startDate || !dates.endDate) return
        const from = new Date(dates.startDate)
        const to = new Date(dates.endDate)
        if (sTime) { const [h, m] = sTime.split(':'); from.setHours(+h, +m, 0, 0) } else { from.setHours(0, 0, 0, 0) }
        if (eTime) { const [h, m] = eTime.split(':'); to.setHours(+h, +m, 59, 0) } else { to.setHours(23, 59, 59, 0) }
        if (to <= from) {
            enqueueSnackbar('End must be after start (check the start/end time).', { variant: 'warning' })
            return
        }
        setChip('custom')
        const withTime = sTime || eTime
        const label = withTime
            ? `${formatDate1(from, 'dd MMM')} ${sTime || '00:00'} – ${formatDate1(to, 'dd MMM')} ${eTime || '23:59'}`
            : `${formatDate1(from, 'dd MMM')} – ${formatDate1(to, 'dd MMM yyyy')}`
        setRange({ from, to, label })
    }

    // Picking dates only records them; the range is committed (and refetched) when Apply is clicked.
    const applyCustom = (r) => {
        if (!r || !r.length || !r[0].startDate || !r[0].endDate) return
        setCustomDates({ startDate: r[0].startDate, endDate: r[0].endDate })
    }

    // Single loader. Args are passed explicitly (not read from state) to avoid stale closures.
    // isBase = the unfiltered load for a window; it also (re)captures the filter option universe.
    const loadMatrix = useCallback((r, mFilter, cFilter, isBase) => {
        setLoading(true)
        post('tools/order/creation-latency/matrix', {
            fromDate: formatDate1(r.from, API_FMT),
            toDate: formatDate1(r.to, API_FMT),
            merchantOids: mFilter,
            courierParents: cFilter,
        })
            .then((res) => {
                const obj = res.data?.responseObj || null
                setData(obj)
                if (isBase) {
                    setOptionMerchants(obj?.merchants || [])
                    setOptionCouriers(obj?.couriers || [])
                }
            })
            .catch(() => enqueueSnackbar('Failed to load latency matrix', { variant: 'error' }))
            .finally(() => setLoading(false))
    }, [enqueueSnackbar])

    // Window change → reset filters and do the base (unfiltered) load.
    useEffect(() => {
        setMerchantFilter(EMPTY_IDS)
        setCourierFilter(EMPTY_IDS)
        loadMatrix(range, EMPTY_IDS, EMPTY_IDS, true)
    }, [range, loadMatrix])

    // Filter change → refetch the matrix for the selection (keeps the option universe).
    const applyMerchantFilter = (ids) => { setMerchantFilter(ids); loadMatrix(range, ids, courierFilter, false) }
    const applyCourierFilter = (ids) => { setCourierFilter(ids); loadMatrix(range, merchantFilter, ids, false) }

    // index cells/aggregates for O(1) lookup while rendering
    const { cellMap, merchantAgg, courierAgg } = useMemo(() => {
        const cellMap = {}, merchantAgg = {}, courierAgg = {}
        if (data) {
            (data.cells || []).forEach((c) => { cellMap[`${c.merchantOid}|${c.courierId}`] = c.stat })
            ;(data.merchantAggregates || []).forEach((a) => { merchantAgg[a.id] = a.stat })
            ;(data.courierAggregates || []).forEach((a) => { courierAgg[a.id] = a.stat })
        }
        return { cellMap, merchantAgg, courierAgg }
    }, [data])

    const downloadAwbs = (merchantOid, courierId, label) => {
        const key = `${merchantOid}|${courierId}`
        if (downloadingKey) return // a download is already in flight
        setDownloadingKey(key)
        // An "All" dimension maps to the active filter (empty array = truly all); a specific cell maps to
        // just that id — so the CSV matches the on-screen cell.
        const merchantOids = merchantOid === ALL ? merchantFilter : [merchantOid]
        const courierParents = courierId === ALL ? courierFilter : [courierId]
        post('tools/order/creation-latency/awbs', {
            fromDate: formatDate1(range.from, API_FMT),
            toDate: formatDate1(range.to, API_FMT),
            merchantOids,
            courierParents,
        })
            .then((res) => {
                const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `awb_latency_${label.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.csv`
                document.body.appendChild(a); a.click(); a.remove()
                URL.revokeObjectURL(url)
            })
            .catch(() => enqueueSnackbar('Failed to download AWB list', { variant: 'error' }))
            .finally(() => setDownloadingKey(null))
    }

    const couriers = data?.couriers || []   // already filtered server-side
    const merchants = data?.merchants || []
    const hasData = merchants.length > 0 && couriers.length > 0
    const hasOptions = optionMerchants.length > 0 || optionCouriers.length > 0

    // one matrix cell: stat block + clickable shipment count
    const StatCell = ({ stat, merchantOid, courierId, label, agg }) => {
        if (!stat) return <TableCell sx={{ color: 'text.disabled', textAlign: 'center' }}>—</TableCell>
        const b = BAND[agg ? 'agg' : bandOf(stat.avgMs)]
        return (
            <TableCell sx={{ bgcolor: b.bg, borderLeft: `3px solid ${b.bar}`, verticalAlign: 'top', minWidth: 150 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 16, color: b.ink, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtNum(stat.avgMs)} <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>ms avg</Typography>
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                    min <b>{fmtNum(stat.minMs)}</b> · max <b>{fmtNum(stat.maxMs)}</b><br />
                    P90 <b>{fmtNum(stat.p90Ms)}</b> · P95 <b>{fmtNum(stat.p95Ms)}</b> · P99 <b>{fmtNum(stat.p99Ms)}</b> · P99.9 <b>{fmtNum(stat.p999Ms)}</b>
                </Typography>
                {(() => {
                    const isDl = downloadingKey === `${merchantOid}|${courierId}`
                    return (
                        <Tooltip title={isDl ? 'Preparing AWB list…' : `Download AWB list — ${label}`}>
                            <Chip
                                size="small"
                                icon={isDl
                                    ? <CircularProgress size={11} thickness={6} sx={{ ml: '5px', color: 'text.secondary' }} />
                                    : <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
                                label={isDl ? 'preparing…' : `${fmtNum(stat.shipments)} shipments`}
                                onClick={() => { if (!isDl) downloadAwbs(merchantOid, courierId, label) }}
                                sx={{ mt: 0.75, fontSize: 10.5, height: 22, cursor: isDl ? 'default' : 'pointer' }}
                            />
                        </Tooltip>
                    )
                })()}
            </TableCell>
        )
    }

    return (
        <>
            <PageTitle>Order Creation Latency</PageTitle>
            <Page title="">
                {/* ── filters ── */}
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        {/* Row 1 — quick preset ranges */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
                            <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 74 }}>Time range</Typography>
                            {CHIPS.map((c) => (
                                <Chip
                                    key={c.key}
                                    label={c.label}
                                    size="small"
                                    color={chip === c.key ? 'primary' : 'default'}
                                    variant={chip === c.key ? 'filled' : 'outlined'}
                                    onClick={() => applyChip(c)}
                                />
                            ))}
                        </Stack>

                        {/* Row 2 — custom date range + optional start/end time */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap sx={{ mt: 1.25 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 74 }}>Custom</Typography>
                            <DateRangePicker
                                defaultRange={[{ startDate: range.from, endDate: range.to, key: 'selection' }]}
                                maxDate={new Date()}
                                onChange={applyCustom}
                            />
                            <TextField
                                type="time" size="small" label="Start time" InputLabelProps={{ shrink: true }}
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                sx={{ width: 128 }}
                            />
                            <TextField
                                type="time" size="small" label="End time" InputLabelProps={{ shrink: true }}
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                sx={{ width: 128 }}
                            />
                            <Button
                                size="small" variant="outlined" disabled={!customDates}
                                onClick={() => buildCustomRange(customDates, startTime, endTime)}
                            >
                                Apply
                            </Button>
                        </Stack>

                        {/* Row 3 — dimension filters (applied server-side; refetch keeps values exact) */}
                        {hasOptions && (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
                                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 74 }}>Filter</Typography>
                                    <Autocomplete
                                        multiple size="small" options={optionMerchants} getOptionLabel={(o) => o.name}
                                        value={optionMerchants.filter((m) => merchantFilter.includes(m.id))}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        onChange={(e, val) => applyMerchantFilter(val.map((v) => v.id))}
                                        renderInput={(params) => <TextField {...params} label="Merchants" placeholder="All merchants" />}
                                        sx={{ minWidth: 240, flex: 1 }}
                                    />
                                    <Autocomplete
                                        multiple size="small" options={optionCouriers} getOptionLabel={(o) => o.name}
                                        value={optionCouriers.filter((c) => courierFilter.includes(c.id))}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        onChange={(e, val) => applyCourierFilter(val.map((v) => v.id))}
                                        renderInput={(params) => <TextField {...params} label="Couriers" placeholder="All couriers" />}
                                        sx={{ minWidth: 240, flex: 1 }}
                                    />
                                </Stack>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* ── trend (additive; above the matrix) ── */}
                <OrderCreationLatencyTrend range={range} merchantOids={merchantFilter} courierParents={courierFilter} />

                {/* ── matrix ── */}
                <Card>
                    <CardContent>
                        {loading ? (
                            <Box>
                                <Skeleton variant="rounded" height={38} sx={{ mb: 1 }} />
                                {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 0.75 }} />)}
                            </Box>
                        ) : !hasData ? (
                            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                                No order-creation latency recorded for this window / selection.
                            </Typography>
                        ) : (
                            <>
                                <Stack direction="row" spacing={2} sx={{ mb: 1.5 }} flexWrap="wrap">
                                    <Legend band={BAND.good} text="Healthy — avg ≤ 2s" />
                                    <Legend band={BAND.warn} text="Elevated — 2–5s" />
                                    <Legend band={BAND.crit} text="Critical — > 5s" />
                                </Stack>
                                <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                                <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                    <TableHead>
                                        <TableRow>
                                            <HeadCell corner>Merchant \ Courier</HeadCell>
                                            {couriers.map((c) => <HeadCell key={c.id}>{c.name}</HeadCell>)}
                                            <HeadCell>All Couriers</HeadCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {merchants.map((m) => (
                                            <TableRow key={m.id}>
                                                <RowHead name={m.name} sub="merchant" />
                                                {couriers.map((c) => (
                                                    <StatCell
                                                        key={c.id}
                                                        stat={cellMap[`${m.id}|${c.id}`]}
                                                        merchantOid={m.id}
                                                        courierId={c.id}
                                                        label={`${m.name} × ${c.name}`}
                                                    />
                                                ))}
                                                <StatCell
                                                    agg
                                                    stat={merchantAgg[m.id]}
                                                    merchantOid={m.id}
                                                    courierId={ALL}
                                                    label={`${m.name} × All couriers`}
                                                />
                                            </TableRow>
                                        ))}
                                        {/* per-courier aggregates + grand total */}
                                        <TableRow>
                                            <RowHead name="All Merchants" sub="courier overall" agg />
                                            {couriers.map((c) => (
                                                <StatCell
                                                    key={c.id}
                                                    agg
                                                    stat={courierAgg[c.id]}
                                                    merchantOid={ALL}
                                                    courierId={c.id}
                                                    label={`All merchants × ${c.name}`}
                                                />
                                            ))}
                                            <StatCell
                                                agg
                                                stat={data.grandTotal}
                                                merchantOid={ALL}
                                                courierId={ALL}
                                                label="All merchants × All couriers"
                                            />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                                </TableContainer>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Page>
        </>
    )
}

// Coloured chip with the label inside — same fill/stripe as a matrix cell so the legend matches the table.
const Legend = ({ band, text }) => (
    <Box sx={{
        bgcolor: band.bg, color: band.ink, borderLeft: `3px solid ${band.bar}`, borderRadius: '4px',
        px: 1, py: 0.4, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
        {text}
    </Box>
)

const HeadCell = ({ children, corner }) => (
    <TableCell
        sx={{
            bgcolor: '#232b3a', color: '#e8ecf3', fontWeight: 600, fontSize: 12.5,
            whiteSpace: 'nowrap', textAlign: corner ? 'left' : 'center',
            position: corner ? 'sticky' : 'static', left: corner ? 0 : 'auto', zIndex: corner ? 3 : 2,
        }}
    >
        {children}
    </TableCell>
)

const RowHead = ({ name, sub, agg }) => (
    <TableCell
        sx={{
            position: 'sticky', left: 0, zIndex: 1, whiteSpace: 'nowrap',
            bgcolor: agg ? BAND.agg.bg : '#fff', fontWeight: 600, borderRight: '2px solid #e3e7ec',
        }}
    >
        {name}
        <Typography component="span" sx={{ display: 'block', fontWeight: 400, fontSize: 11, color: 'text.disabled' }}>
            {sub}
        </Typography>
    </TableCell>
)
